import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycAgentCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { AgentDto } from "@/features/kyc/api/dto";
import type { AgentStatus } from "@/features/shared/api/enums";

/**
 * Active, désactive ou suspend un agent
 * (`POST /Agents/{id}/status?status=...` — statut en query param, guide
 * §6.2). Un agent suspendu ne peut plus ni enrôler ni opérer de cash.
 */
export const useSetAgentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "agents", "set-status"],
    mutationFn: ({
      agentId,
      status,
    }: {
      agentId: string;
      status: AgentStatus;
    }) =>
      mutateData({
        url: `${kycEndpoints.agents.setStatus(agentId)}?status=${status}`,
        method: "POST",
      }) as Promise<AgentDto>,
    onSuccess: (_data, { agentId }) => {
      return invalidateAfterMutation(queryClient, kycAgentCacheKeys(agentId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSetAgentStatus failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
